import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { SidekickSummaryDetail } from '../../../../models/sidekick-summary.model';
import { BaseComponent } from '../../../base/base.component';
import { ChartConfig } from '../../../core/chart/chart.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { SidekickChartComponent } from './sidekick-chart.component';

@Component({
  selector: 'app-pr-summary',
  imports: [
    DetailItemComponent,
    DetailPanelComponent,
    CardComponent,
    SidekickChartComponent,
  ],
  template: ` <div class="tw-flex tw-gap-4">
    <div [class]="showFinancials ? 'tw-basis-2/3' : 'tw-basis-full'">
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-full">
          <app-detail-panel [dataSource]="dataSource">
            <app-detail-item
              label="Type of damages"
              name="typeOfInjury"
              labelPosition="Top"
            />
            <app-detail-item
              label="Claim status"
              name="claimStatus"
              labelPosition="Top"
            />
            <app-detail-item
              label="Summary of actions"
              name="summaryOfActions"
              labelPosition="Top"
            />
            <app-detail-item
              label="Path to resolution"
              name="pathToResolution"
              labelPosition="Top"
            />
          </app-detail-panel>
        </div>
      </div>
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-full">
          <div class="tw-basis-full">
            <app-card label="Claim Details">
              <div class="tw-flex tw-gap-8 tw-pb-2">
                <div class="tw-basis-1/2">
                  <app-detail-panel [dataSource]="dataSource">
                    <app-detail-item label="Client Name" name="clientName" />
                    @if (showAccountName) {
                      <app-detail-item
                        label="Account Name"
                        name="accountName"
                      />
                    }
                    <app-detail-item label="Claimant" name="claimant" />
                    @if (showClaimantDateOfBirth) {
                      <app-detail-item
                        label="Claimant date of birth"
                        name="claimantDateOfBirth"
                        format="d"
                      />
                    }
                    <app-detail-item label="Claim Type" name="claimType" />
                    <app-detail-item label="Cause Of Loss" name="causeOfLoss" />
                    <app-detail-item
                      label="Policy Number"
                      name="policyNumber"
                    />
                    <app-detail-item label="Subrogation" name="subrogation" />
                    <app-detail-item
                      label="Property Location"
                      name="lossLocation"
                    />
                  </app-detail-panel>
                </div>
                <div class="tw-basis-1/2">
                  <app-detail-panel [dataSource]="dataSource">
                    <app-detail-item
                      label="Date Prepared"
                      name="datePrepared"
                      format="d"
                    />
                    <app-detail-item
                      label="Date Of Loss"
                      name="dateOfLoss"
                      format="d"
                    />
                    <app-detail-item
                      label="Claims Examiner"
                      name="claimsExaminer"
                    />
                    <app-detail-item label="Claim Number" name="claimNumber" />
                    @if (showDateClaimReported) {
                      <app-detail-item
                        label="Date Claim Reported"
                        name="dateClaimReported"
                        format="d"
                      />
                    }
                    <app-detail-item label="Status" name="status" />
                    @if (showDateClosed) {
                      <app-detail-item
                        label="Date of Claim Closure"
                        name="dateClosed"
                        format="d"
                      />
                    }
                  </app-detail-panel>
                </div>
              </div>
            </app-card>
          </div>
        </div>
      </div>
    </div>
    @if (showFinancials) {
      <div class="tw-basis-1/3">
        <div class="tw-flex tw-pl-8 tw-flex-col tw-pb-4">
          <app-sidekick-chart
            [dataSource]="indemnityDataSource"
            title="Loss"
          />
          <app-sidekick-chart
            [dataSource]="expenseDataSource"
            title="Expense"
          />
          <app-sidekick-chart [dataSource]="totalsDataSource" />
        </div>
      </div>
    }
  </div>`,
  styles: [],
})
export class PrSummaryComponent extends BaseComponent implements OnInit {
  @ViewChildren(SidekickChartComponent) private chartComponents!: QueryList<SidekickChartComponent>;

  showAccountName = true;
  showClaimantDateOfBirth = true;
  showDateClaimReported = true;
  showDateClosed = true;
  showFinancials = true;

  private _dataSource: SidekickSummaryDetail | null = null;

  @Input()
  set dataSource(value: SidekickSummaryDetail | null) {
    this._dataSource = value;
    if (value) {
      this.initializeCharts();
    }
  }

  get dataSource(): SidekickSummaryDetail | null {
    return this._dataSource;
  }

  expenseDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  totalsDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  indemnityDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };

  ngOnInit(): void {
    this.initFieldSecurity();
    this.showFinancials = this.session.authorize('ClaimSummaryIncurred');

    if (this._dataSource) {
      this.initializeCharts();
    }
  }

  private initFieldSecurity(): void {
    const restricted = this.session.user?.fieldSecurity?.restrictedFields || [];
    this.showAccountName = !restricted.includes('AccountName');
    this.showClaimantDateOfBirth = !restricted.includes('DateOfBirth');
    this.showDateClaimReported = !restricted.includes('DateClaimReported');
    this.showDateClosed = !restricted.includes('DateClosed');
  }

  initializeCharts(): void {
    const paidColor = '#3b3cc2';
    const reserveColor = '#08857d';

    if (this._dataSource?.financials) {
      const financials = this._dataSource.financials;

      // Indemnity/Loss chart
      this.indemnityDataSource.configData = new ChartConfig();
      this.indemnityDataSource.configData.showLabels = false;
      this.indemnityDataSource.configData.addSeries(
        'Paid',
        financials.indemnityPaid || 0,
        paidColor,
      );
      this.indemnityDataSource.configData.addSeries(
        'Reserve',
        financials.indemnityReserve || 0,
        reserveColor,
      );
      this.indemnityDataSource.totalIncurred =
        financials.indemnityIncurred || 0;
      this.indemnityDataSource.firstPartyDeductible =
        financials.indemnityDeductible || 0;

      // Expense chart
      this.expenseDataSource.configData = new ChartConfig();
      this.expenseDataSource.configData.showLabels = false;
      this.expenseDataSource.configData.addSeries(
        'Paid',
        financials.expenseOtherPaid || 0,
        paidColor,
      );
      this.expenseDataSource.configData.addSeries(
        'Reserve',
        financials.expenseOtherReserve || 0,
        reserveColor,
      );
      this.expenseDataSource.totalIncurred =
        financials.expenseOtherIncurred || 0;
      this.expenseDataSource.firstPartyDeductible =
        financials.expenseDeductible || 0;

      // Totals chart
      this.totalsDataSource.configData = new ChartConfig();
      this.totalsDataSource.configData.showLabels = false;
      this.totalsDataSource.configData.addSeries(
        'Paid',
        financials.totalPaid || 0,
        paidColor,
      );
      this.totalsDataSource.configData.addSeries(
        'Reserve',
        financials.totalReserve || 0,
        reserveColor,
      );
      this.totalsDataSource.totalIncurred = financials.totalIncurred || 0;
      this.totalsDataSource.firstPartyDeductible =
        financials.totalDeductible || 0;
    }
  }

  captureFinancialChartImages(): string[] {
    return this.chartComponents
      .map((chart) => chart.captureDonutImage())
      .filter((image): image is string => image != null && image.length > 0);
  }
}
